/**
 * The `SocialRoom` connection (design/v2.md §2.3).
 *
 * Joined once from `AppShell`, for the whole signed-in session, and **kept while
 * the player is at a table** — the room route lives outside the shell, so this
 * hook is mounted by a component that both trees render. That is the point of
 * the feature: an invite has to reach somebody who is already playing.
 *
 * Everything arrives as an addressed message rather than as shared state. There
 * is nothing here that reads `room.state` except the aggregate headcount,
 * because the schema deliberately contains nothing else (see the server's
 * `schema/SocialState.ts`).
 */

import type { Room } from 'colyseus.js';
import { useEffect } from 'react';

import {
  SOCIAL_ROOM_NAME,
  type Friend,
  type FriendRequest,
  type PresenceState,
  type RoomInvite,
} from '@ordago/shared';

import { useStore } from '../store';

import { colyseus } from './colyseus';
import { friendsApi } from './friends';

let socialRoom: Room<unknown> | null = null;

/**
 * Sends `room.invite` on the *table's* room, not on this one. Exported from here
 * so every social affordance is in one file, but the message really does belong
 * to `MusRoom`: only the table knows whether there is a free seat.
 */
export function heldSocialRoom(): Room<unknown> | null {
  return socialRoom;
}

export function useSocialRoom(): void {
  const token = useStore((s) => s.auth.accessToken);

  useEffect(() => {
    if (token === null) return;

    let cancelled = false;
    let room: Room<unknown> | undefined;
    useStore.getState().social.setConnection('connecting');

    void (async () => {
      try {
        const joined = await colyseus().joinOrCreate(SOCIAL_ROOM_NAME, { token });
        if (cancelled) {
          void joined.leave();
          return;
        }
        room = joined;
        socialRoom = joined;

        joined.onMessage<{ friends: Friend[]; pendingIncoming: number }>('social.hello', (msg) => {
          useStore.getState().social.setFriends(msg.friends);
          // `pendingIncoming` is why the greeting carries a count at all: a
          // request sent while you were signed out arrives through no message,
          // so without this the navbar badge stayed at zero until somebody
          // happened to open the panel. One REST call, and only when there is
          // something to fetch.
          if (msg.pendingIncoming > 0) {
            void friendsApi
              .requests()
              .then((requests) => {
                useStore.getState().social.setRequests(requests.incoming, requests.outgoing);
              })
              .catch(() => undefined);
          }
        });

        joined.onMessage<{ userId: string; state: PresenceState }>('friend.presence', (msg) => {
          useStore.getState().social.setPresence(msg.userId, msg.state);
        });

        joined.onMessage<{ request: FriendRequest }>('friend.request', (msg) => {
          useStore.getState().social.addIncoming(msg.request);
        });

        joined.onMessage<{ friend: Friend }>('friend.accepted', (msg) => {
          useStore.getState().social.addFriend(msg.friend);
        });

        joined.onMessage<{ userId: string }>('friend.removed', (msg) => {
          useStore.getState().social.removeFriend(msg.userId);
        });

        joined.onMessage<RoomInvite>('room.invited', (msg) => {
          useStore.getState().social.pushInvite(msg);
        });

        joined.onLeave(() => {
          useStore.getState().social.setConnection('closed');
        });

        useStore.getState().social.setConnection('open');
      } catch {
        // Presence is a convenience. A failed join must leave the lobby, the
        // table and the friends REST calls all working.
        if (!cancelled) useStore.getState().social.setConnection('closed');
      }
    })();

    return () => {
      cancelled = true;
      socialRoom = null;
      void room?.leave();
      useStore.getState().social.reset();
    };
  }, [token]);
}
